import type { CSETextInput } from '@cloudsuite/mosaic-elements'
import { required } from 'components/shared/required/required.js'
import { html, LitElement, nothing, type CSSResultGroup } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { CSCBasketLineMemo } from 'components/ordering/basket-line-memo/basket-line-memo.js'
import { webshopCommunicator, type WeightCalculationResponse, type StoreFrontApiError } from './webshop-communicator.js'
import { is } from 'typia'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { style } from './composable-weight-calculator.style.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { UpdateProductConfigurationEvent, UpdateProductConfigurationEventFinished } from './composable-weight-calculator-update-button.js'
import type { OrderFormButtonState } from 'components/ordering/order-form/components/types.js'
import { ProductAddedEvent } from 'components/ordering/add-to-basket/events.js'
import { classMap } from 'lit/directives/class-map.js'
import { HiddenFieldsMixin } from 'components/shared/hidden-fields/hidden-fields-mixin.js'

type CalculationType = 'LXW' | 'L'

export const composableWeightCalculatorHiddenFields = ['quantity', 'helper-texts'] as const
export type ComposableWeightCalculatorHiddenField = typeof composableWeightCalculatorHiddenFields[number]

const API_ERROR_CODE_FIELD_MAP = {
  21: 'base',
  22: 'base',
  23: 'weight',
  24: 'length',
  25: 'width'
}

@customElement('csc-composable-weight-calculator')
export class CSCComposableWeightCalculator extends HiddenFieldsMixin(LitElement, composableWeightCalculatorHiddenFields) {
  static override styles: CSSResultGroup = [hostStyle, style]

  @property({ attribute: 'calculation-type' })
  public calculationType: CalculationType = 'L'

  @property({ attribute: 'maximum-product-length', type: Number })
  public maximumProductLength: number | undefined

  @property({ attribute: 'maximum-product-width', type: Number })
  public maximumProductWidth: number | undefined

  @property({ attribute: 'can-order', type: Boolean, reflect: true })
  public canOrder = false

  @property({ attribute: 'basket-line-id', type: Number })
  public basketLineId?: number

  @required()
  @property({ attribute: 'product-id', type: Number })
  public productId!: number

  @required()
  @property({ attribute: 'dimension-uom' })
  public dimensionUom!: string

  @property({ attribute: 'weight-uom' })
  public weightUom: string | undefined

  @required()
  @property({ attribute: 'basket-id' })
  public basketId!: string

  @property({ attribute: 'sale-price' })
  public salePrice: string | undefined

  @required()
  @property({ attribute: 'unit-price' })
  public unitPrice!: string

  @property({ attribute: 'initial-product-length', type: Number })
  public initialProductLength: number | undefined

  @property({ attribute: 'initial-product-width', type: Number })
  public initialProductWidth: number | undefined

  @property({ attribute: 'initial-weight', type: Number })
  public initialWeight?: number

  @property({ attribute: 'initial-quantity', type: Number })
  public initialQuantity?: number

  @query('slot[name="prefix"]')
  private prefixSlot?: HTMLSlotElement

  @state()
  public lengthState = 0

  @state()
  public quantityState = 1

  @state()
  public widthState: null | number = this.calculationType === 'L' ? null : 0

  @state()
  private _calculationResult?: WeightCalculationResponse | null

  @state()
  public calculationError?: { field: string, message: string } | null = null

  @state()
  public error?: string | undefined

  @state()
  public isCalculatingWeight = false

  @state()
  public buttonState: OrderFormButtonState = 'error'

  private buttonResetTimeout?: ReturnType<typeof setTimeout>

  get calculationResult() {
    return this._calculationResult
  }

  get weightFromCalculationResult() {
    return is<WeightCalculationResponse>(this.calculationResult) ? this.calculationResult.weight : ''
  }

  get weightFieldValue() {
    if (this._calculationResult === undefined) return this.initialWeight ?? ''

    return this.weightFromCalculationResult
  }

  private get memoElement() {
    return this.prefixSlot?.assignedElements({ flatten: true })
      .find((element): element is CSCBasketLineMemo => element instanceof CSCBasketLineMemo)
  }

  get requiredDimensionsFilled() {
    const dimensions = this.getDimensions()

    if (!dimensions.length) return false

    return this.calculationType === 'LXW' ? Boolean(dimensions.width) : true
  }

  public getDimensions() {
    return {
      length: this.lengthState,
      width: this.widthState,
      quantity: this.quantityState,
    }
  }

  private updateDimension(dimension: 'length' | 'width' | 'quantity', value: number) {
    this[`${dimension}State`] = value
  }

  private resetCalculation() {
    this._calculationResult = null
    this.calculationError = null
    this.error = undefined
    this.isCalculatingWeight = false
    this.buttonState = 'error'
  }

  private async handleFieldChange(e: Event, key: 'length' | 'width' | 'quantity') {
    const target = e.target as CSETextInput

    this.updateDimension(key, Number(target.value))

    if (!this.requiredDimensionsFilled) {
      this.resetCalculation()
      return
    }

    try {
      await this.fetchWeight()
    } catch { /* noop */ }
  }

  public constructSearchParams() {
    const dimensions = this.getDimensions()
    const params = new URLSearchParams({ length: dimensions.length.toString(), quantity: dimensions.quantity.toString() })
    if (this.calculationType === 'LXW') {
      params.append('width', `${dimensions.width}`)
    }

    return params
  }

  private parseError(error: StoreFrontApiError) {
    const errorCode = error.api_error_code as keyof typeof API_ERROR_CODE_FIELD_MAP

    const field = API_ERROR_CODE_FIELD_MAP[errorCode] ?? 'base'

    return {
      field,
      message: error.description
    }
  }

  public async fetchWeight() {
    const searchParams = this.constructSearchParams()
    this.isCalculatingWeight = true
    this.buttonState = 'error'
    const result = await webshopCommunicator.calculateWeight(this.productId, searchParams)

    if (!this.isConnected || !this.requiredDimensionsFilled) return

    if (is<StoreFrontApiError>(result)) {
      this.calculationError = this.parseError(result)
      this.error = result.message
      this.isCalculatingWeight = false
      this._calculationResult = null
      this.buttonState = 'error'
      return
    }

    this.calculationError = null
    this.isCalculatingWeight = false
    this._calculationResult = result
    this.buttonState = 'initial'
  }

  private async handleAddToBasket() {
    const configurator_data = this.getDimensions()
    const memo = this.memoElement?.value ?? ''

    this.buttonState = 'pending'

    try {
      const response = await webshopCommunicator.addToBasket(this.basketId, this.productId, configurator_data, memo)

      if (!this.isConnected) return

      document.dispatchEvent(new ProductAddedEvent(response.items.length))

      if (response.items) {
        this.buttonState = 'confirmed'
      }

      clearTimeout(this.buttonResetTimeout)
      this.buttonResetTimeout = setTimeout(() => {
        this.buttonState = 'initial'
      }, 1500)
    } catch { /* noop */ }
  }

  private renderErrorStateForField(field: string) {
    if (!this.calculationError || this.calculationError.field !== field) {
      return nothing
    }

    return html`<div class="invalid-text" slot="invalid-text">${this.calculationError.message}</div>`
  }

  private renderHelptextForField(field: string) {
    if (this.isHidden('helper-texts')) return nothing

    if (field === 'length' && this.maximumProductLength) {
      return html`<div class="help-text" slot="help-text">
        ${t('help_text_length', { ns: 'csc-composable-weight-calculator', default: 'Max {{variable_max}} {{variable_uom}}', variable_max: this.maximumProductLength, variable_uom: this.dimensionUom })}
      </div>`
    }

    if (field === 'width' && this.maximumProductWidth) {
      return html`<div class="help-text" slot="help-text">
        ${t('help_text_width', { ns: 'csc-composable-weight-calculator', default: 'Max {{variable_max}} {{variable_uom}}', variable_max: this.maximumProductWidth, variable_uom: this.dimensionUom })}
      </div>`
    }

    return nothing
  }

  private handleUpdateEvent = async (event: UpdateProductConfigurationEvent) => {
    if (event.basketLineId !== this.basketLineId) {
      return
    }

    const dimensions = {
      length: this.lengthState,
      width: this.widthState ?? this.initialProductWidth ?? null,
      quantity: this.quantityState
    }

    const memo = this.memoElement?.value

    try {
      await webshopCommunicator.updateBasket(this.basketId, this.basketLineId, dimensions, memo)

      if (!this.isConnected) return

      this.dispatchEvent(new UpdateProductConfigurationEventFinished())
    } catch { /* noop */ }
  }

  private addListeners() {
    document.addEventListener(UpdateProductConfigurationEvent.type, this.handleUpdateEvent)
  }

  override connectedCallback(): void {
    super.connectedCallback()
    this.addListeners()

    this.lengthState = this.initialProductLength ?? this.lengthState
    this.widthState = this.initialProductWidth ?? this.widthState
    this.quantityState = this.initialQuantity ?? this.quantityState
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback()
    document.removeEventListener(UpdateProductConfigurationEvent.type, this.handleUpdateEvent)
    clearTimeout(this.buttonResetTimeout)
  }

  override render() {
    if (!(this.basketId && this.productId && this.dimensionUom && this.unitPrice)) {
      return nothing
    }

    return html`
      <section class="composable-weight-calculator">
        <slot name="prefix"></slot>
        <cse-paper variant="secondary">
          <div class="content">
            <cse-text-group>
              <cse-text slot="heading" class="title" variant="heading-4" as="h2">${t('title', { ns: 'csc-composable-weight-calculator', default: 'Calculate your price' })}</cse-text>
              <cse-text class="subtitle" variant="body">${t('subtitle', { ns: 'csc-composable-weight-calculator', default: 'Enter the size you need to see the weight and price.' })}</cse-text>
            </cse-text-group>

            <cse-fieldset>
              <cse-form-field id="composable-weight-input-length">
                <cse-label for="length">
                  ${t('label_length', { ns: 'csc-composable-weight-calculator', default: 'Length ({{variable_uom}})', variable_uom: this.dimensionUom })}
                </cse-label>
                <cse-text-input
                  @change="${(e: Event) => this.handleFieldChange(e, 'length')}"
                  type="number"
                  value="${this.initialProductLength ?? ''}"
                  name="length"
                  id="length"
                  .max="${this.maximumProductLength ?? undefined}"
                  min="1"
                ></cse-text-input>
                ${this.renderHelptextForField('length')}
                ${this.renderErrorStateForField('length')}
              </cse-form-field>

              ${this.calculationType === 'LXW' ? html`
                <cse-form-field id="composable-weight-input-width">
                  <cse-label for="width">
                    ${t('label_width', { ns: 'csc-composable-weight-calculator', default: 'Width ({{variable_uom}})', variable_uom: this.dimensionUom })}
                  </cse-label>
                  <cse-text-input
                    @change="${(e: Event) => this.handleFieldChange(e, 'width')}"
                    type="number"
                    value="${this.initialProductWidth ?? ''}"
                    name="width"
                    id="width"
                    .max="${this.maximumProductWidth ?? undefined}"
                    min="1"
                  ></cse-text-input>
                  ${this.renderHelptextForField('width')}
                  ${this.renderErrorStateForField('width')}
                </cse-form-field>
              ` : nothing}

              <cse-form-field id="composable-weight-input-weight">
                <cse-label for="weight">
                  ${t('label_weight', { ns: 'csc-composable-weight-calculator', default: 'Weight ({{variable_uom}})', variable_uom: this.weightUom })}
                </cse-label>
                <cse-text-input value="${this.weightFieldValue}" name="weight" id="weight" type="number" disabled></cse-text-input>
              </cse-form-field>
            </cse-fieldset>

            <div class="prices">
            ${this.isCalculatingWeight ? html`<cse-skeleton variant="secondary" height="34"></cse-skeleton>` :
          html`${this.calculationResult?.amount ? html`
                  <cse-price class="total-price" variant="default" size="l" layout="inline" vat="${t('exluding_vat', { ns: 'csc-composable-weight-calculator', default:'Excl. VAT' })}">
                    ${this.calculationResult.amount}
                  </cse-price>
                ` : html`
                  <cse-price class="total-price" variant="default" size="l" layout="inline" vat="">
                    ${this.salePrice || t('price_to_be_calculated', { ns: 'csc-composable-weight-calculator', default: 'Price to be calculated' })}
                  </cse-price>
                `}
              `}
              
              ${this.isCalculatingWeight ?
          html`<cse-skeleton variant="secondary" height="24"></cse-skeleton>` :
          html`
                  <cse-price class="unit-price" variant="default" size="s" layout="inline" uom="${this.weightUom}">
                    ${this._calculationResult?.price ?? this.unitPrice}
                  </cse-price>
              `}
            </div>
              <div class="order-form">
                <cse-stepper-input
                  min="1"
                  name="quantity"
                  type="number"
                  id="quantity"
                  min="1"
                  value="${this.initialQuantity || 1}"
                  @change="${(e: Event) => this.handleFieldChange(e, 'quantity')}"
                  class="${classMap({
                    'hidden': this.isHidden('quantity')
                  })}"
                ></cse-stepper-input>
                ${this.canOrder ? html`
                  <csc-order-form-button state="${this.buttonState}" variant="secondary" @order-form-add="${this.handleAddToBasket}"></csc-order-form-button>
                ` : nothing}
              </div>
          </div>
        </cse-paper>
      </section>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-composable-weight-calculator': CSCComposableWeightCalculator
  }
}
